import './types';
// this is assumed namespace from '@types/segment-analytics'
// eslint-disable-next-line
import SegmentAnalytics from '@segment/analytics.js-core';

/**
 * Track an analytics event using Segment
 *
 * This function is a wrapper around window.analytics.track that standardizes
 * event naming and provides type safety.
 *
 * @param eventName - The name of the event to track (without 'FT' prefix)
 * @param properties - Optional properties to include with the event
 * @param options - Optional segment options
 * @param callback - Optional callback function
 */
export function trackEvent(
  eventName: string,
  properties?: Record<string, any>,
  options?: SegmentAnalytics.SegmentOpts,
  callback?: () => void
): void {
  // Add 'FT' prefix to event names for consistency
  const prefixedEventName = eventName.startsWith('FT ')
    ? eventName
    : `FT ${eventName}`;
  if (window.analytics) {
    window.analytics.track(prefixedEventName, properties, options, callback);
  }
  if (process.env.NODE_ENV !== 'production') {
    console.log(
      `[Segment] Tracking event: ${prefixedEventName}`,
      properties,
      options
    );
  }
}

/**
 * Track a page view using Segment
 *
 * This function is a wrapper around window.analytics.page that provides type safety.
 *
 * @param properties - Optional properties to include with the page view
 * @param options - Optional segment options
 * @param callback - Optional callback function
 */
export function trackPage(
  properties?: Record<string, any>,
  options?: SegmentAnalytics.SegmentOpts,
  callback?: () => void
): void {
  if (window.analytics) {
    window.analytics.page(properties, options, callback);
  }
}

/**
 * Get the current user's anonymous ID
 *
 * @returns The anonymous ID of the current user, or undefined if not available
 */
export function getAnonymousId(): string | undefined {
  if (window.analytics && window.analytics.user) {
    return window.analytics.user().anonymousId();
  }
  return undefined;
}

/**
 * Get the current user's ID
 *
 * @returns The ID of the current user, or undefined if not available
 */
export function getUserId(): string | undefined {
  if (window.analytics && window.analytics.user) {
    return window.analytics.user().id() || undefined;
  }
  return undefined;
}

/**
 * Execute a callback when analytics is ready
 *
 * @param callback - The function to execute when analytics is ready
 */
export function onAnalyticsReady(callback: () => void): void {
  if (window.analytics) {
    window.analytics.ready(callback);
  } else {
    // If analytics is not available, execute the callback immediately
    callback();
  }
}
